/* eslint-disable react-hooks/exhaustive-deps */
import { ThemeProvider } from "@emotion/react";
import MaterialTable, {
  Icons,
  MaterialTableProps,
  MTableBodyRow,
} from "@material-table/core";
import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@mui/icons-material";
import { createTheme } from "@mui/material";
import { STheme } from "components/styles";
import {
  createContext,
  CSSProperties,
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

type Props<T extends object> = MaterialTableProps<T> &
  Partial<{
    keyName: string;
    width: number | string;
    height: number | string;
    pageSize: number;
    multi: boolean;
    edit: boolean;
    fixed: number;
    handleDoubleClick: (rowData: T) => void;
    handleSetDisabled: (rowData: T) => boolean;
    rowSelectable: boolean;
  }>;

export const TableSelectContext = createContext(
  {} as [any, Dispatch<SetStateAction<any>>]
);

export function STable<T extends object>({
  keyName = "id",
  data,
  columns,
  tableRef,
  edit,
  width = 800,
  height = 360,
  pageSize = 10,
  fixed,
  handleDoubleClick,
  handleSetDisabled,
  rowSelectable = true,
  ...props
}: Props<T>) {
  const [selectedId, setSelectedId] = useState<number>(0);

  const handleRowDoubleClick = (_: any, rowData: any) => {
    setSelectedId(rowData && keyName in rowData ? rowData[keyName] : 0);
    handleDoubleClick?.call(null, rowData);
  };

  useEffect(() => {
    if (edit) {
      tableRef?.current.dataManager.changeBulkEditOpen(true);
    }
  }, []);

  useImperativeHandle(
    tableRef,
    () => ({
      ...tableRef?.current,
      handleClearSelect: () => setSelectedId(0),
    }),
    []
  );

  const theme = createTheme(
    {
      components: {
        MuiTable: {
          styleOverrides: {
            root: {
              height: "fit-content",
            },
          },
        },
        MuiTableBody: {
          styleOverrides: {
            root: {
              "& .MuiTableRow-root": {
                height: "25px !important",
              },
            },
          },
        },
        MuiTableFooter: {
          styleOverrides: {
            root: {
              "& .MuiTablePagination-toolbar": {
                minHeight: "30px",
              },
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              backgroundColor: STheme.tables.row,
            },
            hover: {
              backgroundColor: STheme.tables.hover,
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              fontSize: "0.8rem",
              padding: 0,
            },
            paddingCheckbox: {
              padding: 0,
            },
            paddingNone: {
              padding: 0,
            },
            sizeSmall: {
              paddingTop: "1px",
              paddingBottom: "0px",
            },
            body: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              paddingLeft: "10px",
              paddingRight: "10px",
              paddingTop: "1px",
              paddingBottom: "1px",
            },
          },
        },
        MuiTablePagination: {
          styleOverrides: {
            toolbar: {
              minHeight: "0px",
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            popper: {
              visibility: "collapse",
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            elevation: {
              "> .MuiBox-root": {
                height: height,
                "> div > div": {
                  overflow: fixed ? "auto" : "visible !important",
                },
              },
            },
          },
        },
      },
    },
    { ...STheme }
  );

  return (
    <ThemeProvider theme={theme}>
      <MaterialTable
        {...props}
        data={data}
        columns={columns}
        components={{
          Row: (props) => <MTableBodyRow {...props} onDoubleClick={() => {}} />,
          OverlayLoading: (props) => <></>,
        }}
        style={{
          whiteSpace: "nowrap",
          width: width,
        }}
        options={{
          headerStyle: headerStyle,
          rowStyle: (data) => ({
            padding: 0,
            backgroundColor:
              keyName in data && selectedId === data[keyName]
                ? STheme.tables.select
                : STheme.tables.row,
          }),
          showDetailPanelIcon: false,
          pageSizeOptions: [pageSize],
          pageSize: pageSize,
          sorting: false,
          draggable: false,
          fixedColumns: { left: fixed },
          search: false,
          showEmptyDataSourceMessage: false,
          padding: "dense",
          showTitle: false,
          selection: props.multi,
          selectionProps: (data: T) => ({
            disabled: handleSetDisabled?.call(null, data),
          }),
          toolbar: false,
          detailPanelType: "single",
          tableLayout: "fixed",
        }}
        icons={icons}
        tableRef={tableRef}
        onRowClick={(event, rowData: any) => {
          if (!rowSelectable) return;
          setSelectedId(rowData && keyName in rowData ? rowData[keyName] : 0);
          props.onRowClick?.call(null, event, rowData);
        }}
        onRowDoubleClick={handleRowDoubleClick}
        onBulkEditOpen={() => {
          tableRef?.current.dataManager.changeBulkEditOpen(true);
        }}
      />
    </ThemeProvider>
  );
}

const headerStyle: CSSProperties = {
  backgroundColor: STheme.tables.header,
  position: "sticky",
  fontSize: "0.75rem",
  top: 0,
  paddingLeft: "1px",
  paddingRight: "1px",
  paddingTop: "1px",
  paddingBottom: "1px",
  whiteSpace: "nowrap",
  color: STheme.tables.text,
  resize: "horizontal",
  overflowX: "hidden",
  overflowY: "hidden",
  zIndex: 10,
};

const icons: Icons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};
